// src/helpers/GetAuthor.js

import axios from "axios";
import qs from "qs";

const API_URL = process.env.NEXT_PUBLIC_SERVER_URL || "";

// ─────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────

/** Normalize Strapi v4 relation or v5 flat object → plain { id, ...fields } */
function normalizeRelation(rel) {
  if (!rel) return null;
  if (rel?.data?.attributes) return { id: rel.data.id, ...rel.data.attributes };
  if (rel?.data) return rel.data;
  return rel;
}

/** Normalize Strapi v4 media or v5 flat → { url, alternativeText } */
function normalizeMedia(media) {
  if (!media) return null;
  if (media?.data?.attributes) {
    return {
      url: media.data.attributes.url || null,
      alternativeText: media.data.attributes.alternativeText || null,
    };
  }
  if (media?.url) {
    return { url: media.url, alternativeText: media.alternativeText || null };
  }
  return null;
}

/**
 * Normalize a raw Strapi article item (v4 or v5) into the shape
 * expected by BlogCards
 */
function normalizeArticle(item) {
  const attrs = item.attributes ? item.attributes : item;
  const id = item.id ?? attrs.id;

  return {
    id,
    title: attrs.title ?? null,
    slug: attrs.slug ?? null,
    description: attrs.description ?? null,
    blogDate: attrs.blogDate ?? null,
    publishedAt: attrs.publishedAt ?? null,
    updatedAt: attrs.updatedAt ?? null,
    cover: normalizeMedia(attrs.cover),
    category: normalizeRelation(attrs.category),
    author: normalizeRelation(attrs.author),
    blocks: attrs.blocks ?? [],
  };
}

// ─────────────────────────────────────────────
// Exported functions
// ─────────────────────────────────────────────

/**
 * Fetch a single author by their slug.
 */
export async function fetchAuthorBySlug(slug) {
  const query = qs.stringify(
    {
      filters: { slug: { $eq: slug } },
      populate: { avatar: { fields: ["url", "alternativeText"] } },
    },
    { encodeValuesOnly: true },
  );

  const res = await axios.get(`${API_URL}/authors?${query}`);
  const raw = res.data?.data?.[0];
  if (!raw) return null;

  if (raw.attributes) {
    const { avatar, ...rest } = raw.attributes;
    return { id: raw.id, ...rest, avatar: normalizeMedia(avatar) };
  }

  return raw;
}


// GetAuthor.js
export const AUTHOR_PAGE_SIZE = 12;

export async function fetchArticlesByAuthor(authorId, page, pageSize) {
    const query = qs.stringify(
        {
            filters: {
                author: { id: { $eq: authorId } },
            },
            fields: ["title", "slug", "description", "blogDate", "publishedAt", "updatedAt"],
            populate: {
                cover:    { fields: ["url", "alternativeText"] },
                category: { fields: ["name"] },
                author:   { fields: ["name"] },
                blocks:   true,
            },
            pagination: { page, pageSize },
            sort: ["publishedAt:desc"],
        },
        { encodeValuesOnly: true }
    );

    const res = await axios.get(`${API_URL}/articles?${query}`);
    const pagination = res.data?.meta?.pagination ?? {};

    return {
        articles:   (res.data?.data ?? []).map(normalizeArticle),
        totalCount: pagination.total ?? 0,
        pageCount:  pagination.pageCount ?? 1,
    };
}
